import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import times from 'lodash/times';
import Truncate from 'react-truncate';
import StarFill from '@deliveroo/icon-set/react/StarFill';
import P from '@deliveroo/consumer-component-library/components/P';
import Span from '@deliveroo/consumer-component-library/components/Span';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import styles from './styles.scss';

/**
 * A generic star rating-based customer review, with automatic truncation.
 */
export default class CustomerReview extends React.Component {
  state = {
    isExpanded: false,
    isTruncated: false,
    hasLoaded: false,
  };

  componentDidMount = () => {
    this.setState({ hasLoaded: true });
  };

  onTruncate = (isTruncated) => {
    const { hasLoaded } = this.state;
    if (hasLoaded) {
      this.setState({ isTruncated });
    }
  };

  getRatingStars = () => {
    const { rating } = this.props;
    const stars = [];

    times(rating, (index) => {
      stars.push(
        <span key={`star-${index}`} className={styles.starFilled}>
          <Icon component={StarFill} size={18} />
        </span>,
      );
    });

    times(5 - stars.length, (index) => {
      stars.push(
        <span key={`star-${stars.length + index}`} className={styles.starEmpty}>
          <Icon component={StarFill} size={18} />
        </span>,
      );
    });

    return stars;
  };

  onExpandClick = (e) => {
    e.preventDefault();
    this.setState({ isExpanded: true });
  };

  render() {
    const { reviewerName, content, readMoreLinkText, truncateLines } = this.props;
    const { isExpanded, isTruncated, hasLoaded } = this.state;

    const seeMore = isTruncated &&
      !isExpanded && (
        <Fragment>
          &hellip;&nbsp;
          <a href="#see-more" className={styles.seeMoreLink} onClick={this.onExpandClick}>
            {readMoreLinkText}
          </a>
        </Fragment>
      );

    return (
      <div className={styles.review}>
        <div className={styles.rating}>
          {this.getRatingStars()}
          <div className={styles.reviewer}>
            <Span color="grey" size="small">
              {reviewerName}
            </Span>
          </div>
        </div>

        <P adjacentVerticalSpacing="small">
          <Truncate
            lines={hasLoaded && !isExpanded && truncateLines}
            onTruncate={this.onTruncate}
            ellipsis={seeMore}
          >
            {content}
          </Truncate>
        </P>
      </div>
    );
  }
}

CustomerReview.propTypes = {
  /**
   * The star rating, out of 5
   */
  rating: PropTypes.number.isRequired,

  /**
   * The reviewer name — can also include e.g. a date string.
   */
  reviewerName: PropTypes.string.isRequired,

  /**
   * The content of the review
   */
  content: PropTypes.string.isRequired,

  /**
   * The link text displayed when a review has been truncated, to expand the review.
   */
  readMoreLinkText: PropTypes.string.isRequired,

  /**
   * Number of lines of text above which the review should be truncated.
   */
  truncateLines: PropTypes.number,
};

CustomerReview.defaultProps = {
  truncateLines: 2,
};
